/**
 * Main App component for ClarCV application.
 *
 * Sets up routing, authentication, and UI language (ES/EN).
 * Marketing pages live under /es and /en; the product app stays unprefixed.
 */

import React from 'react';
import { BrowserRouter as Router, Navigate, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { AuthGateProvider } from './contexts/AuthGateContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import LocaleLayout from './components/LocaleLayout';
import LegacyLocaleRedirect, {
  LegacyBlogRedirect,
} from './components/LegacyLocaleRedirect';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import FromFile from './pages/FromFile';
import CVUpload from './pages/CVUpload';
import CVAnalysis from './pages/CVAnalysis';
import FastResults from './pages/FastResults';
import PDFImprovement from './pages/PDFImprovement';
import WordImprovement from './pages/WordImprovement';
import CVEditDashboard from './pages/CVEditDashboard';
import QuickEdit from './pages/QuickEdit';
import { FROM_FILE, KEEP_LOOK_ENABLED } from './utils/paths';
import KeepLook from './pages/KeepLook';
import Settings from './pages/Settings';
import PasswordResetConfirm from './pages/PasswordResetConfirm';
import ActivateAccount from './pages/ActivateAccount';
import Pricing from './pages/Pricing';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import BlogList from './pages/BlogList';
import BlogPost from './pages/BlogPost';
import NotFound from './pages/NotFound';
import ProtectedRoute from './components/ProtectedRoute';
import ScrollToTop from './components/ScrollToTop';
import Analytics from './components/Analytics';
import CookieBanner from './components/CookieBanner';
import SkipLink from './components/SkipLink';
import { DEFAULT_LOCALE, readStoredLocale } from './i18n/localeBootstrap';

function RootRedirect() {
  return <Navigate to={`/${readStoredLocale() || DEFAULT_LOCALE}`} replace />;
}

function App() {
  return (
    <Router>
      <LanguageProvider>
        <AuthProvider>
          <AuthGateProvider>
            <ScrollToTop />
            <Analytics />
            <CookieBanner />
            <div className="App">
              <SkipLink />
              <Navbar />
              <main id="main-content" className="App-main" tabIndex={-1}>
                <Routes>
                  <Route path="/" element={<RootRedirect />} />

                  {/* Legacy unprefixed marketing URLs → /es|/en */}
                  <Route path="/pricing" element={<LegacyLocaleRedirect to="/pricing" />} />
                  <Route path="/blog" element={<LegacyLocaleRedirect to="/blog" />} />
                  <Route path="/blog/:slug" element={<LegacyBlogRedirect />} />
                  <Route path="/privacy" element={<LegacyLocaleRedirect to="/privacy" />} />
                  <Route path="/terms" element={<LegacyLocaleRedirect to="/terms" />} />

                  <Route path="/:lang" element={<LocaleLayout />}>
                    <Route index element={<Landing />} />
                    <Route path="pricing" element={<Pricing />} />
                    <Route path="blog" element={<BlogList />} />
                    <Route path="blog/:slug" element={<BlogPost />} />
                    <Route path="privacy" element={<Privacy />} />
                    <Route path="terms" element={<Terms />} />
                    <Route path="*" element={<NotFound />} />
                  </Route>

                  <Route path="/login" element={<Login />} />
                  <Route
                    path="/password/reset/confirm/:uid/:token"
                    element={<PasswordResetConfirm />}
                  />
                  <Route path="/activate/:uid/:token" element={<ActivateAccount />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/dashboard/from-file" element={<FromFile />} />
                  <Route path="/dashboard/upload" element={<CVUpload />} />
                  <Route
                    path="/dashboard/cv/:cvId"
                    element={
                      <ProtectedRoute>
                        <CVAnalysis />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/dashboard/fast-results" element={<FastResults />} />
                  <Route path="/dashboard/pdf-improvement" element={<PDFImprovement />} />
                  <Route path="/dashboard/word-improvement" element={<WordImprovement />} />
                  <Route path="/dashboard/edit-cv" element={<CVEditDashboard />} />
                  <Route path="/dashboard/quick-edit" element={<QuickEdit />} />
                  <Route
                    path="/dashboard/keep-look"
                    element={
                      KEEP_LOOK_ENABLED ? (
                        <KeepLook />
                      ) : (
                        <Navigate to={FROM_FILE} replace />
                      )
                    }
                  />
                  <Route
                    path="/dashboard/settings"
                    element={
                      <ProtectedRoute>
                        <Settings />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </main>
              <Footer />
              <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
              />
            </div>
          </AuthGateProvider>
        </AuthProvider>
      </LanguageProvider>
    </Router>
  );
}

export default App;
